// Warm Inbound Watch — client-side pageview tracker.
// Sends consented pageviews to the Supabase `track-pageview` edge function
// so server-side detection can identify MQL-signature visitors.
//
// SSG-safe: every entry point guards on `typeof window === 'undefined'`.
// Consent-gated: only sends if the CookieConsent component recorded 'accepted'.
// Fail-silent: tracking errors never break the site.

const STORAGE_CLIENT_ID = 'ezcloud_client_id';
const STORAGE_SESSION_ID = 'ezcloud_session_id';
const STORAGE_SESSION_START = 'ezcloud_session_start';
const STORAGE_LAST_PAGE_TS = 'ezcloud_last_page_ts';
const STORAGE_RETURNING = 'ezcloud_returning';
const COOKIE_CONSENT_KEY = 'cookie-consent';

// New session if no activity for 30 minutes
const SESSION_TIMEOUT_MS = 30 * 60 * 1000;
// Cap engagement at 30 min so an open-but-idle tab doesn't skew numbers
const MAX_ENGAGEMENT_MS = 30 * 60 * 1000;

function safeGetItem(key: string): string | null {
  if (typeof window === 'undefined') return null;
  try { return window.localStorage.getItem(key); } catch { return null; }
}

function safeSetItem(key: string, value: string): void {
  if (typeof window === 'undefined') return;
  try { window.localStorage.setItem(key, value); } catch { /* quota / private mode */ }
}

function isConsentGranted(): boolean {
  return safeGetItem(COOKIE_CONSENT_KEY) === 'accepted';
}

function getOrCreateClientId(): string {
  let id = safeGetItem(STORAGE_CLIENT_ID);
  if (!id) {
    id = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `c-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    safeSetItem(STORAGE_CLIENT_ID, id);
  }
  return id;
}

function getOrRotateSession(): { sessionId: string; isFirstVisit: boolean } {
  const lastTs = parseInt(safeGetItem(STORAGE_SESSION_START) || '0', 10);
  const existingId = safeGetItem(STORAGE_SESSION_ID);
  const now = Date.now();

  if (!existingId || !lastTs || (now - lastTs) > SESSION_TIMEOUT_MS) {
    const newId = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `s-${now}-${Math.random().toString(36).slice(2)}`;
    safeSetItem(STORAGE_SESSION_ID, newId);
    safeSetItem(STORAGE_SESSION_START, now.toString());
    const isFirstVisit = !safeGetItem(STORAGE_RETURNING);
    safeSetItem(STORAGE_RETURNING, '1');
    return { sessionId: newId, isFirstVisit };
  }

  // Refresh activity timestamp so session stays alive
  safeSetItem(STORAGE_SESSION_START, now.toString());
  return { sessionId: existingId, isFirstVisit: false };
}

/**
 * Track a pageview. Call on every route change.
 * No-op if window is unavailable (SSG) or consent isn't granted.
 */
export async function trackPageView(pagePath: string, pageTitle?: string): Promise<void> {
  if (typeof window === 'undefined') return;
  if (!isConsentGranted()) return;

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
  const supabaseKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined;
  if (!supabaseUrl || !supabaseKey) return;

  const clientId = getOrCreateClientId();
  const { sessionId, isFirstVisit } = getOrRotateSession();

  // Engagement = time since last pageview from this client (best-effort,
  // captured client-side because the previous page's leave event isn't
  // reliable cross-browser)
  const now = Date.now();
  const lastPageTs = parseInt(safeGetItem(STORAGE_LAST_PAGE_TS) || '0', 10);
  const engagementMs = lastPageTs ? Math.min(now - lastPageTs, MAX_ENGAGEMENT_MS) : 0;
  safeSetItem(STORAGE_LAST_PAGE_TS, now.toString());

  try {
    await fetch(`${supabaseUrl}/functions/v1/track-pageview`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: supabaseKey,
        Authorization: `Bearer ${supabaseKey}`,
      },
      body: JSON.stringify({
        client_id: clientId,
        session_id: sessionId,
        page_path: pagePath,
        page_title: pageTitle || (typeof document !== 'undefined' ? document.title : null),
        referrer: (typeof document !== 'undefined' && document.referrer) ? document.referrer : null,
        engagement_ms: engagementMs,
        is_first_visit: isFirstVisit,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        language: typeof navigator !== 'undefined' ? navigator.language : null,
      }),
      keepalive: true,
    });
  } catch {
    // Fail silently. Tracking failures must never disrupt the user.
  }
}
